import MediaViewerModel from './media-viewer-model';
import type {Directory, Identified} from 'typings/portaro.be.types';
import type {IQService, IScope, ITimeoutService} from 'angular';
import type RepositoryService from './repository.service';
import type {DirectoryDataService} from '../commons/data-services/directory.data-service';
import type {UrlStateManagerService} from '../commons/services/url-state-manager.service';
import type {ViewMode} from './media-viewer-viewer/kp-media-viewer-viewer.component';

export default class MediaViewerService<T extends Identified<number> = Identified<number>> {
    public static serviceName = 'mediaViewerService';

    public thisCurrentDirectory: Directory = null;
    private model: MediaViewerModel<T>;
    private thisDirectoryTree = null;
    private viewMode: ViewMode = 'ORIGINAL';

    /*@ngInject*/
    constructor(private urlStateManagerService: UrlStateManagerService,
                private $timeout: ITimeoutService,
                private $q: IQService,
                private directoryDataService: DirectoryDataService,
                private repositoryService: RepositoryService,
                private $rootScope: IScope) {
        this.model = new MediaViewerModel(this.previous.bind(this), this.next.bind(this));
        urlStateManagerService.register(this.onStateChange.bind(this));
    }

    public current(): T {
        return this.model.current();
    }

    public files(): T[] {
        return this.model.files();
    }

    public hasPrevious(): boolean {
        return this.model.hasPrevious();
    }

    public hasNext(): boolean {
        return this.model.hasNext();
    }

    public async requestCurrent(file: T) {
        await this.urlStateManagerService.requestChangeState({file: file.id});
    }

    public setCurrent(file) {
        this.model.setCurrentFile(file);
        this.repositoryService.addFileToMemory(file, 'current');

        this.$timeout(() => {
            if (this.model.hasPrevious()) {
                this.repositoryService.addFileToMemory(this.model.getPrevious(), 'previous');
            }
            if (this.model.hasNext()) {
                this.repositoryService.addFileToMemory(this.model.getNext(), 'next');
            }
        }, 1000);

        return this.$q.when(file);
    }

    public setFirstAsCurrent() {
        if (this.model.hasFiles()) {
            this.requestCurrent(this.model.files()[0]);
        }
    }

    public previous() {
        if (this.model.hasPrevious()) {
            this.requestCurrent(this.model.getPrevious());
        }
    }

    public next() {
        if (this.model.hasNext()) {
            this.requestCurrent(this.model.getNext());
        }
    }

    public requestCurrentDirectoryAndSetFirstFile(directory) {
        this.urlStateManagerService.requestChangeState({directory: directory.id});
    }

    public hasDirectoryTree() {
        if (!this.thisDirectoryTree) {
            return false;
        }

        return this.thisDirectoryTree.children.length !== 0;
    }

    public currentDirectory() {
        return this.thisCurrentDirectory;
    }

    public directoryTree() {
        return this.thisDirectoryTree;
    }

    public setDirectoryTree(obj) {
        this.thisDirectoryTree = obj;
    }

    public getViewMode() {
        return this.viewMode;
    }

    public setViewMode(viewMode) {
        this.viewMode = viewMode;
    }

    private async onStateChange(params) {
        let rootDirectoryId: number;
        let directoryId: number;
        let fileId;

        if (params.file) {
            fileId = parseInt(params.file, 10);
        }

        if (params.directory) {
            directoryId = parseInt(params.directory, 10);
        }

        if (params.rootDirectory) {
            rootDirectoryId = parseInt(params.rootDirectory, 10);
        }

        let directoryPromise;
        let filesPromise;
        let filePromise;

        // FILE PROMISE
        if (fileId) {
            filePromise = this.repositoryService
                              .getFileById(fileId)
                              .then(this.setCurrent.bind(this));
        }

        // DIRECTORY PROMISE
        // find first dir from tree if exists, else get root dir if exists
        if (directoryId) { // we have directory id in url
            directoryPromise = this.directoryDataService.getDirectoryById(directoryId)
                                   .then((dir) => {
                                       this.thisCurrentDirectory = dir;
                                       return this.thisCurrentDirectory;
                                   });

        } else if (filePromise) { // we have file (fileId in url) so get its dir
            directoryPromise = filePromise
                .then((file) => {
                    this.thisCurrentDirectory = file.directory;
                    return this.thisCurrentDirectory;
                });

        } else if (this.thisDirectoryTree) { // we have directory tree so find first dir
            const getFirstDirectoryFromTree = (directory) => {
                if (directory.children) {
                    for (const child of directory.children) {
                        if (child) {
                            return getFirstDirectoryFromTree(child);
                        }
                    }
                }

                if (directory) {
                    return directory;
                }

                return null;
            };

            directoryPromise = this.$q.when(getFirstDirectoryFromTree(this.thisDirectoryTree))
                                   .then((dir) => {
                                       this.thisCurrentDirectory = dir;
                                       return this.thisCurrentDirectory;
                                   });

        } else if (rootDirectoryId) { // we have rootDir
            directoryPromise = this.directoryDataService.getDirectoryById(rootDirectoryId)
                                   .then((dir) => {
                                       this.thisCurrentDirectory = dir;
                                       return this.thisCurrentDirectory;
                                   });
        }

        // FILES PROMISE (if we have directory promise)
        if (directoryPromise) {
            filesPromise = directoryPromise
                .then((directory) => directory.id)
                .then(this.repositoryService.getFilesByDirectory.bind(this.repositoryService))
                .then((files) => {
                    this.model.setFiles(files);
                    return files;
                });
        }

        // FILE PROMISE AGAIN if we have not it in url, get it from files promise
        if (!filePromise && filesPromise) {
            filePromise = filesPromise
                .then((files) => {
                    if (files.length === 0) {
                        this.model.unsetCurrent();
                        return null;
                    }
                    return this.setCurrent(files[0]);
                });
        }

        const promisesToResolve = [];
        if (directoryPromise) {
            promisesToResolve.push(directoryPromise);
        }
        if (filesPromise) {
            promisesToResolve.push(filesPromise);
        }
        if (filePromise) {
            promisesToResolve.push(filePromise);
        }

        const fileLoadedPromise = this.$q.all(promisesToResolve);

        return fileLoadedPromise.then(() => {
            this.$rootScope.$broadcast('mediaViewer.fileLoaded');
        });
    }
}
